import { jobCategoryTree } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useJobOverlay } from '../lib/job-overlay.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../lib/trpc.js'
import { ClientPicker, type PickedClient } from './ClientPicker.js'
import { DialogFooter, DialogHeader, Modal } from './Modal.js'
import { SitePicker } from './SitePicker.js'
import { Badge, Button, Field, Select, Textarea, humanise } from './ui.js'

type Template = RouterOutputs['jobs']['templates'][number]
type Category = RouterOutputs['jobs']['categories'][number]

/**
 * Setting a job up.
 *
 * Client, then site, then category — and the brief, the checklist, the
 * duration, the price and the recurrence arrive prefilled from the category's
 * template. That is the whole point of decision 41: the office was already
 * doing this by pasting a skeleton into the job description and typing over
 * it, and this is the same motion with the answers in fields that can be
 * queried.
 *
 * **A job is not named here, and asking for one was a mistake.** ServiceM8 has
 * no field that maps to `Job.title`, so 74,299 of 74,300 imported jobs have
 * none, and the exception is a test row. What identifies a job is its number,
 * its client and its address; what describes it is the category. Every screen
 * already falls back to those, and `jobTitle` in `routes/Jobs.tsx` exists
 * purely to *discard* titles that repeat the client name. Decision 63.
 *
 * The checklist is shown before saving rather than after. A template that
 * silently adds nine items is a template people are afraid of; one that says
 * what it is about to add is one they correct instead of avoiding.
 *
 * `routes/Jobs.tsx` carried a New job button with no handler since the list
 * was built, and the dispatch board had none at all. Both now open this.
 */
export function NewJobDialog({
  open,
  onClose,
  onCreated,
  client,
}: {
  open: boolean
  onClose: () => void
  /** When set, the job is handed back instead of opening the overlay. */
  onCreated?: (job: { id: string; number: number; displayNumber: string }) => void
  /** Starts the form on this client — a job raised from the client's own page. */
  client?: PickedClient
}) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="new-job-title" className="max-w-3xl">
      {open ? <NewJobForm onClose={onClose} onCreated={onCreated} initialClient={client} /> : null}
    </Modal>
  )
}

function NewJobForm({
  onClose,
  onCreated,
  initialClient,
}: {
  onClose: () => void
  onCreated?: (job: { id: string; number: number; displayNumber: string }) => void
  initialClient?: PickedClient
}) {
  const trpc = useTRPC()
  const { openJob } = useJobOverlay()
  const queryClient = useQueryClient()

  const [client, setClient] = useState<PickedClient | null>(initialClient ?? null)
  const [siteId, setSiteId] = useState('')
  const [categoryId, setCategoryId] = useState('')
  /** Empty means "whatever the category picks"; `none` means deliberately bare. */
  const [templateChoice, setTemplateChoice] = useState('')
  const [scope, setScope] = useState('')

  const categories = useQuery(trpc.jobs.categories.queryOptions())
  const templates = useQuery(trpc.jobs.templates.queryOptions())

  const create = useMutation(
    trpc.jobs.create.mutationOptions({
      onSuccess: (job) => {
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        void queryClient.invalidateQueries(trpc.schedule.pathFilter())
        if (onCreated) {
          // Book the slot before the dialog closes, or the parent would drop
          // the draft it is about to use.
          onCreated(job)
          onClose()
          return
        }
        onClose()
        // The new job opens over the screen it was created from — the Jobs
        // list or the board — rather than taking the person somewhere else.
        openJob(job.id)
      },
    }),
  )

  // Grouped rather than one 40-line list. The office reads "Termite" and then
  // which termite job, which is how they say it out loud.
  const sections = useMemo(() => jobCategoryTree(categories.data ?? []), [categories.data])

  // What the server will resolve, worked out here so the form can show it.
  // Same rule as `resolveTemplate`: a category with two templates picks
  // neither, because guessing between two sets of standing instructions is
  // how a job gets the wrong safety checklist.
  const applied = useMemo(
    () => resolveTemplate(templates.data ?? [], templateChoice, categoryId, categories.data ?? []),
    [templates.data, templateChoice, categoryId, categories.data],
  )

  const itemCount = applied?.sections.reduce((sum, s) => sum + s.items.length, 0) ?? 0

  return (
    <form
      className="flex min-h-0 flex-col"
      onSubmit={(event) => {
        event.preventDefault()
        if (!client) return

        create.mutate({
          clientId: client.id,
          siteId: siteId || null,
          categoryId: categoryId || null,
          ...(templateChoice === 'none'
            ? { templateId: null }
            : templateChoice
              ? { templateId: templateChoice }
              : {}),
          scope: scope.trim() || null,
        })
      }}
    >
      <DialogHeader
        id="new-job-title"
        title="New job"
        description="Pick the client and the kind of work; the rest fills itself in."
        onClose={onClose}
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        <ClientPicker
          id="new-job-client"
          autoFocus
          value={client}
          onChange={(picked) => {
            setClient(picked)
            setSiteId('')
          }}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          {/* Remounted per client, because the picker holds "no site, on
              purpose" as its own state and that answer is about one client's
              properties. */}
          <SitePicker
            key={client?.id ?? 'no-client'}
            id="new-job-site"
            clientId={client?.id ?? null}
            value={siteId}
            onChange={setSiteId}
          />

          <Field label="Kind of work" htmlFor="new-job-category">
            <Select
              id="new-job-category"
              value={categoryId}
              onChange={(event) => {
                setCategoryId(event.target.value)
                setTemplateChoice('')
              }}
            >
              <option value="">Not categorised</option>
              {sections.map((section, index) =>
                section.group ? (
                  <optgroup key={section.group.id} label={section.group.name}>
                    {section.options.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </optgroup>
                ) : (
                  section.options.map((category) => (
                    <option key={`${index}-${category.id}`} value={category.id}>
                      {category.name}
                    </option>
                  ))
                ),
              )}
            </Select>
          </Field>
        </div>

        <TemplatePicker
          templates={templates.data ?? []}
          categories={categories.data ?? []}
          categoryId={categoryId}
          value={templateChoice}
          applied={applied}
          itemCount={itemCount}
          onChange={setTemplateChoice}
        />

        <Field
          label="What to do"
          htmlFor="new-job-scope"
          hint="Leave empty to use the template. Anything typed here wins."
        >
          <Textarea
            id="new-job-scope"
            value={scope}
            onChange={(event) => setScope(event.target.value)}
            placeholder={applied?.scopeTemplate ?? 'The brief for this visit'}
          />
        </Field>

        {create.error ? (
          <p className="text-xs text-red-600" role="alert">
            {errorMessage(create.error)}
          </p>
        ) : null}
      </div>

      <DialogFooter
        hint={
          applied
            ? `${applied.name} · ${itemCount} checklist ${itemCount === 1 ? 'item' : 'items'}`
            : 'No template. Everything can be filled in on the job.'
        }
      >
        <Button type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={create.isPending} disabled={!client}>
          Create job
        </Button>
      </DialogFooter>
    </form>
  )
}

/**
 * Which template will be applied, computed the same way the server computes
 * it. Duplicated deliberately rather than fetched: the form has to say what
 * is about to happen *before* it happens, and asking the server would be a
 * round trip per keystroke of the category dropdown.
 *
 * The parent fallback has to be duplicated too, or the form promises no
 * template and the job arrives with one.
 */
function resolveTemplate(
  templates: Template[],
  choice: string,
  categoryId: string,
  categories: Category[],
): Template | null {
  if (choice === 'none') return null
  if (choice) return templates.find((template) => template.id === choice) ?? null
  if (!categoryId) return null

  for (const id of templateCategoryIds(categoryId, categories)) {
    const matches = templates.filter((template) => template.categoryId === id)
    if (matches.length > 0) return matches.length === 1 ? matches[0]! : null
  }

  return null
}

/**
 * The category itself, then the group it sits in.
 *
 * Ordered, because the near one wins and an ambiguous near one stops the
 * search rather than falling through to the group.
 */
function templateCategoryIds(categoryId: string, categories: Category[]): string[] {
  const parentId = categories.find((category) => category.id === categoryId)?.parentId
  return parentId ? [categoryId, parentId] : [categoryId]
}

function TemplatePicker({
  templates,
  categories,
  categoryId,
  value,
  applied,
  itemCount,
  onChange,
}: {
  templates: Template[]
  categories: Category[]
  categoryId: string
  value: string
  applied: Template | null
  itemCount: number
  onChange: (value: string) => void
}) {
  // The level the answer came from, so the warning counts the templates that
  // are actually competing rather than the ones on the category the person
  // picked, which may have none.
  const forCategory = templateCategoryIds(categoryId, categories)
    .map((id) => templates.filter((template) => template.categoryId === id))
    .find((matches) => matches.length > 0)

  const ambiguous = !value && (forCategory?.length ?? 0) > 1

  return (
    <div className="flex flex-col gap-2 rounded-md bg-ink-50 px-2.5 py-2 ring-1 ring-inset ring-ink-200">
      <Field label="Template" htmlFor="new-job-template">
        <Select id="new-job-template" value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">
            {categoryId ? 'Whatever this category uses' : 'Whatever the category uses'}
          </option>
          {templates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.name}
            </option>
          ))}
          <option value="none">No template — start blank</option>
        </Select>
      </Field>

      {/* Two standing instructions for one category is a question, not a
          default, so it is asked rather than resolved quietly. */}
      {ambiguous ? (
        <p className="text-xs font-medium text-ink-800">
          {forCategory?.length} templates use this category. Pick one, or the job is created
          without any.
        </p>
      ) : null}

      {applied ? (
        <div className="flex flex-col gap-1">
          {applied.sections.map((section) => (
            <div key={section.id} className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium text-ink-700">{section.name}</span>
              <Badge tone={section.audience === 'TECH' ? 'outline' : 'neutral'}>
                {humanise(section.audience)}
              </Badge>
              <span className="text-[11px] text-ink-400 tabular-nums">
                {section.items.length}
              </span>
            </div>
          ))}
          {itemCount === 0 ? (
            <p className="text-xs text-ink-500">This template has no checklist on it yet.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}